import { z } from "zod";

const clientSchema = z.object({
  NEXT_PUBLIC_APP_ENV: z.enum([
    "local",
    "development",
    "staging",
    "production",
  ]),
  NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().min(1),
  NEXT_PUBLIC_MAIN_API_URL: z.url(),
  NEXT_PUBLIC_IMAGES_URL: z.url(),
  NEXT_PUBLIC_SENTRY_RELEASE: z.string().optional(),
  NEXT_PUBLIC_GTM_ID: z.string().optional(),
  NEXT_PUBLIC_UMAMI_WEBSITE_ID: z.string().optional(),
  NEXT_PUBLIC_UMAMI_HOST: z.string().optional(),
  NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: z.string().min(1),
  /** AI graphics editor feature flags. All optional, default off /
   * conservative. Flip per environment after the related code + static
   * assets land. */
  /** point AI editor's iframes at the same-origin vendored Tailwind
   * Play (`/static/tailwind-play.js`) instead of `cdn.tailwindcss.com`. Flip
   * true AFTER `pnpm --filter @restomat/admin run vendor:tailwind` has
   * produced the file AND it's deployed. */
  NEXT_PUBLIC_USE_VENDORED_TAILWIND: z.enum(["true", "false"]).optional(),
  /** override the default 1-round LLM verify cap. Range [1,5]. */
  NEXT_PUBLIC_AI_VERIFY_ROUNDS: z.string().regex(/^\d+$/).optional(),
  /**
   * Origin of the backend SignalR hub (incl. path), e.g.
   * `https://restomat.cz/api/main/v1/hubs/ai-graphic-jobs`. Optional —
   * when unset, the client derives it from `NEXT_PUBLIC_MAIN_API_URL`.
   */
  NEXT_PUBLIC_BACKEND_HUB_URL: z.string().optional(),
});

const serverSchema = z.object({
  AUTH0_BASE_URL: z.url(),
  AUTH0_DOMAIN: z.string().min(1),
  AUTH0_CLIENT_ID: z.string().min(1),
  AUTH0_CLIENT_SECRET: z.string().min(1),
  AUTH0_SECRET: z.string().min(1),
  AUTH0_AUDIENCE: z.url(),

  APP_BASE_URL: z.url(),

  GOOGLE_MAPS_SERVER_KEY: z.string().min(1),

  MAIN_API_URL: z.url(),

  REDIS_HOST: z.string().min(1),
  REDIS_PORT: z.string().min(1),
  REDIS_DB: z.string().min(1),

  RENDERER_SERVICE_URL: z.url(),

  AUTH_URL: z.url(),
  AUTH_FACEBOOK_ID: z.string().min(1),
  AUTH_FACEBOOK_SECRET: z.string().min(1),
  AUTH_GOOGLE_ID: z.string().min(1),
  AUTH_GOOGLE_SECRET: z.string().min(1),
  AUTH_SECRET: z.string().min(1),

  MUX_TOKEN_ID: z.uuid(),
  MUX_TOKEN_SECRET: z.string().min(1),

  NEXT_RUNTIME: z.enum(["nodejs", "edge"]).optional(),
});

const allSchema = z.object({
  ...clientSchema.shape,
  ...serverSchema.shape,
});

type Env = z.infer<typeof allSchema>;

const parseEnv = (): Env => {
  const isServer = typeof window === "undefined";

  if (isServer) {
    const shouldSkipServerValidation =
      !!process.env.SKIP_ENV_VALIDATION ||
      !!process.env.CI ||
      process.env.npm_lifecycle_event === "build";

    if (shouldSkipServerValidation) {
      const clientParsed = clientSchema.safeParse(process.env);

      if (!clientParsed.success) {
        console.error(
          "❌ Invalid client environment variables (CI/Build):",
          JSON.stringify(z.treeifyError(clientParsed.error), null, 2),
        );
        throw new Error("Invalid client environment variables");
      }

      return { ...process.env, ...clientParsed.data } as unknown as Env;
    }

    const parsed = allSchema.safeParse(process.env);

    if (!parsed.success) {
      console.error(
        "❌ Invalid environment variables (Server):",
        JSON.stringify(z.treeifyError(parsed.error), null, 2),
      );
      throw new Error("Invalid environment variables");
    }

    return parsed.data as Env;
  }

  const clientEnv = {
    NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV,
    NEXT_PUBLIC_GOOGLE_MAPS_API_KEY:
      process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
    NEXT_PUBLIC_MAIN_API_URL: process.env.NEXT_PUBLIC_MAIN_API_URL,
    NEXT_PUBLIC_IMAGES_URL: process.env.NEXT_PUBLIC_IMAGES_URL,
    NEXT_PUBLIC_SENTRY_RELEASE: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_UMAMI_WEBSITE_ID: process.env.NEXT_PUBLIC_UMAMI_WEBSITE_ID,
    NEXT_PUBLIC_UMAMI_HOST: process.env.NEXT_PUBLIC_UMAMI_HOST,
    NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY:
      process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    NEXT_PUBLIC_USE_VENDORED_TAILWIND:
      process.env.NEXT_PUBLIC_USE_VENDORED_TAILWIND,
    NEXT_PUBLIC_AI_VERIFY_ROUNDS: process.env.NEXT_PUBLIC_AI_VERIFY_ROUNDS,
    NEXT_PUBLIC_BACKEND_HUB_URL: process.env.NEXT_PUBLIC_BACKEND_HUB_URL,
  };

  const parsed = clientSchema.safeParse(clientEnv);

  if (!parsed.success) {
    console.error(
      "❌ Invalid environment variables (Client):",
      JSON.stringify(z.treeifyError(parsed.error), null, 2),
    );
    throw new Error("Invalid environment variables");
  }

  return parsed.data as unknown as Env;
};

export const env = parseEnv();
